import { Routes, Route, Navigate } from 'react-router-dom';
import Passenger from '../components/home/passengers/Passenger';
import Passengers from '../components/home/passengers/Passengers';
import ReservationDetailsPage from '../components/home/reservations/ReservationDetailsPage';
import { CreateReservationPage } from '../components/reservations/CreateReservationPage';
import Client from '../components/home/clients/Client';
import Clients from '../components/home/clients/Clients';
import TripDetails from '../components/home/monitoring/TripDetails';
import MonitorTrips from '../components/home/monitoring/MonitorTrips';
import CSASupervisor from '../components/home/csa-supervisor/LandingPage';
import TransportationBenefits from '../components/home/transportation-benefits/TransportationBenefits';
import TransportationAttributes from '../components/home/transportation-attributes/TransportationAttributes';
import Settings from '../components/home/admin/Settings';
import WorkQueues from '../components/home/workqueues/WorkQueues';
import WorkQueue from '../components/home/workqueues/WorkQueue';
import HomePage from '../components/home/home/HomePage';
import ParentOrganizations from '../components/home/parent-organizations/ParentOrganizations';
import ParentOrganization from '../components/home/parent-organizations/ParentOrganization';
import TripLegInventory from '../components/home/triplegs/TripLegInventory';
import { useIsF1User } from '../hooks/permissions/useIsF1User';
import { useCanManageMolinaUsers } from '../hooks/permissions/useCanManageMolinaUsers';
import { useCanReadCSA } from '../hooks/permissions/useCanReadCSA';
import { useCanReadPassengers } from '../hooks/permissions/useCanReadPassengers';
import { useCanReadCSASupervisor } from '../hooks/permissions/useCanReadCSASupervisor';
import { useCanReadAdministration } from '../hooks/permissions/useCanReadAdministration';
import { useCanReadClients } from '../hooks/permissions/useCanReadClients';
import { useCanReadMonitoring } from '../hooks/permissions/useCanReadMonitoring';
import { useCanReadParentOrgs } from '../hooks/permissions/useCanReadParentOrgs';
import { useCanReadPricingModels } from '../hooks/permissions/useCanReadPricingModels';
import { useCanReadSettings } from '../hooks/permissions/useCanReadSettings';
import { useCanReadWorkQueues } from '../hooks/permissions/useCanReadWorkQueues';
import { useCanReadTransportationAttributes } from '../hooks/permissions/useCanReadTransportationAttributes';
import { useCanReadTransportationBenefits } from '../hooks/permissions/useCanReadTransportationBenefits';
import { useCanReadTripInventory } from '../hooks/permissions/useCanReadTripInventory';
import { useCanReadReservationDetails } from '../hooks/permissions/useCanReadReservationDetails';
import { useCanReadPurposeCodes } from '../hooks/permissions/useCanReadPurposeCodes';
import { TodaysTrips } from '../components/home/today/TodaysTrips';
import WorkFlows from '../components/home/workflows/WorkFlows';
import { useCanReadWorkFlows } from '../hooks/permissions/useCanReadWorkFlows';
import { useCanCreateWorkFlow } from '../hooks/permissions/useCanCreateWorkflow';
import EditWorkflow from '../components/home/workflows/EditWorkflow';
import Lookups from '../components/home/admin/Lookups';
import ApplicationGlobalSettings from '../components/home/admin/ApplicationGlobalSettings';
import PermissionsAdmin from '../components/home/admin/PermissionsAdmin';
import { useCanUpdateWorkflows } from '../hooks/permissions/useCanUpdateWorkflow';
import { useLocation } from 'react-router-dom';
import EditSimplifiedWorkflow from '../components/home/workflows/simplified/EditSimplifiedWorkflow';
import SimpleNotifications from '../components/home/workflows/simplified/SimpleNotifications';
import Discussions from '../components/home/monitoring/Discussions';
import SimplifiedWorkflow from '../components/home/workflows/simplified/SimplifiedWorkflow';
import { useCanReadAiServices } from '../hooks/permissions/useCanReadAiServices';
import { useIsPR1User } from '../hooks/permissions/useIsPR1User';
import { useIsPR2User } from '../hooks/permissions/useIsPR2User';
import { useFeatureExceptionResolve } from '../hooks/features/useFeatureExceptionResolve';
import PR2HomePage from '../components/home/home/PR2HomePage';
import ReviewInvoices from '../components/home/invoices/ReviewInvoices';
import Invoices from '../components/home/invoices/Invoices';
import WorkflowExecutionDetails from '../components/home/workflows/WorkflowExecutionDetails';
import ServiceLevelModels from '../components/home/service-level-models/ServiceLevelModels';
import { AiServicesPage } from '../components/home/ai-services/AiServicesPage';
import { PurposeCodes } from '../components/home/admin/PurposeCodes';
import { useCanReadServiceLevelModels } from '../hooks/permissions/useCanReadServiceLevelModels';
import PricingModels from '../components/home/pricingmodels/PricingModels';
import ClientProgramsList from '../components/home/clients/ClientProgramsList';
import { useFeatureReviewInvoicing } from '../hooks/features/useFeatureReviewInvoicing';
import ClientLoader from '../components/home/clients/ClientLoader';
import ClientServiceAreas from '../components/home/clients/ClientServiceAreas';
import ClientTransportationBenefitsList from '../components/home/clients/ClientTransportationBenefits';
import ClientServiceLevels from '../components/home/clients/ClientServiceLevels';
import RideshareSettings from '../components/home/admin/RideshareSettings';
import ClientServicePlansList from '../components/home/clients/ClientServicePlansList';
import { InventoryManagement } from '../components/home/admin/inventory-management/InventoryManagement';
import AdminSettings from '../components/home/admin-settings/AdminSettings';
import MonitoringExceptions from '../components/home/monitoring/MonitoringExceptions';

const HomeRouting = () => {
  const location = useLocation();
  const isF1User = useIsF1User();
  if (!isF1User) {
    return <Navigate to="/noaccess" replace />;
  }

  const canManageMolinaUsers = useCanManageMolinaUsers();
  const canViewCSAPage = useCanReadCSA();
  const canViewCSASupervisorPage = useCanReadCSASupervisor();
  const canViewPassengerPage = useCanReadPassengers();
  const canViewParentOrgPage = useCanReadParentOrgs();
  const canViewClientsPage = useCanReadClients();
  const canViewMonitoringPage = useCanReadMonitoring();
  const canViewPricingModelsPage = useCanReadPricingModels();
  const canViewTransportationBenefitsPage = useCanReadTransportationBenefits();
  const canViewTransportationAttributesPage =
    useCanReadTransportationAttributes();
  const canViewServiceLevelModelsPage = useCanReadServiceLevelModels();
  const canViewAdministrationPage = useCanReadAdministration();
  const canViewWorkQueuesPage = useCanReadWorkQueues();
  const canViewWorkFlowsPage = useCanReadWorkFlows();
  const canViewCreateWorkflowsPage = useCanCreateWorkFlow();
  const canViewEditWorkflowsPage = useCanUpdateWorkflows();
  const canViewTripInventoryPage = useCanReadTripInventory();
  const canViewReservationDetailsPage = useCanReadReservationDetails();
  const canViewSettingsPage = useCanReadSettings();
  const canViewPurposeCodesPage = useCanReadPurposeCodes();
  const canViewAiServicesPage = useCanReadAiServices();
  const isPR1User = useIsPR1User();
  const isPR2User = useIsPR2User();
  const enableReviewInvoicing = useFeatureReviewInvoicing();
  const enableExceptionResolve = useFeatureExceptionResolve();

  const molinaRoutes = (
    <Routes>
      <Route path="/" element={<Navigate to="/settings" replace />} />
      <Route path={'/settings'} element={<Settings />} />
      <Route path="*" element={<Navigate to={'/'} replace />} />
    </Routes>
  );

  const allRoutes = (
    <Routes>
      {/* Set up default route redirects */}
      {/* Default */}
      {canViewPassengerPage && (
        <Route path="/" element={<Navigate to="/passengers" replace />} />
      )}
      {/* CSA Supervisor */}
      {canViewCSASupervisorPage && !canViewPassengerPage && (
        <Route path="/" element={<Navigate to="/csa-supervisor" replace />} />
      )}
      {/* PR1 Home page redirect */}
      {isPR1User && !canViewPassengerPage && (
        <Route path="/" element={<Navigate to="/today" replace />} />
      )}
      {/* PR2 Home page redirect */}
      {isPR2User && !canViewPassengerPage && (
        <Route path="/" element={<Navigate to="/home" replace />} />
      )}
      {/* Redirect to Monitoring if that's all they have access to */}
      {!canViewCSASupervisorPage &&
        !canViewPassengerPage &&
        canViewMonitoringPage && (
          <Route path="/" element={<Navigate to="/monitoring" replace />} />
        )}
      {/* Redirect to Trip Inventory if that's all they have access to */}
      {!canViewCSASupervisorPage &&
        !canViewPassengerPage &&
        !canViewMonitoringPage &&
        canViewTripInventoryPage && (
          <Route path="/" element={<Navigate to="/triplegs" replace />} />
        )}

      {/* Home Page routes */}
      {/* If any user role has a unique home page under the /home route, add it here */}
      {/* CSA */}
      {canViewCSAPage && !isPR2User && (
        <Route path={'/home'} element={<HomePage />} />
      )}
      {/* PR2 */}
      {isPR2User && <Route path={'/home'} element={<PR2HomePage />} />}

      {/* All routes */}
      {/* Passengers */}
      {canViewPassengerPage && (
        <Route path={'/passengers'} element={<Passengers />} />
      )}
      {canViewPassengerPage && (
        <>
          <Route path={'/passengers/:id'} element={<Passenger />} />
          <Route
            path={'/passengers/:id/create-reservation'}
            element={<CreateReservationPage />}
          />
        </>
      )}
      {canViewReservationDetailsPage && (
        <Route
          path={'/passengers/:passengerId/reservations/:referenceId'}
          element={<ReservationDetailsPage />}
        />
      )}

      {/* CSA Supervisor */}
      {canViewCSASupervisorPage && (
        <Route path={'/csa-supervisor'} element={<CSASupervisor />} />
      )}

      {/* Clients */}
      {canViewClientsPage && <Route path={'/clients'} element={<Clients />} />}
      {canViewClientsPage && (
        <Route path={'/clients/:id'} element={<ClientLoader />}>
          <Route index element={<Client />} />
          <Route path="programs" element={<ClientProgramsList />} />
          <Route
            path="transportation-benefits"
            element={<ClientTransportationBenefitsList />}
          />
          <Route path="servicePlans" element={<ClientServicePlansList />} />
          <Route path="serviceAreas" element={<ClientServiceAreas />} />
          <Route path="serviceLevels" element={<ClientServiceLevels />} />
        </Route>
      )}

      {/* Monitoring */}
      {canViewMonitoringPage && (
        <Route
          path={'/monitoring'}
          element={<MonitorTrips key={location.search} />}
        />
      )}
      {canViewMonitoringPage && enableExceptionResolve && (
        <Route
          path={'/monitoring/exceptions'}
          element={<MonitoringExceptions key={location.search} />}
        />
      )}

      {/* Trip Inventory */}
      {canViewTripInventoryPage && (
        <Route path={'/triplegs'} element={<TripLegInventory />} />
      )}
      {canViewTripInventoryPage && (
        <Route path={'/tripdetails'} element={<TripDetails />} />
      )}
      {canViewTripInventoryPage && (
        <Route path={'/today'} element={<TodaysTrips />} />
      )}
      {isPR2User && <Route path={'/discussions'} element={<Discussions />} />}
      {isPR2User && enableReviewInvoicing && (
        <Route path={'/review-invoices'} element={<ReviewInvoices />} />
      )}
      {isPR2User && enableReviewInvoicing && (
        <Route path={'/invoices'} element={<Invoices />} />
      )}

      {/* Administration */}
      {canViewAdministrationPage && (
        <Route path={'/settings'} element={<Settings />} />
      )}
      {canViewAdministrationPage && (
        <Route path={'/admin'} element={<AdminSettings />} />
      )}
      {canViewParentOrgPage && (
        <Route
          path={'/parent-organizations'}
          element={<ParentOrganizations />}
        />
      )}
      {canViewParentOrgPage && (
        <Route
          path={'/parent-organizations/:id'}
          element={<ParentOrganization />}
        />
      )}
      {canViewPricingModelsPage && (
        <Route path={'/pricingmodels'} element={<PricingModels />} />
      )}

      {canViewTransportationAttributesPage && (
        <Route
          path={'/transportation-attributes'}
          element={<TransportationAttributes />}
        />
      )}
      {canViewPurposeCodesPage && (
        <Route path={'/purposecodes'} element={<PurposeCodes />} />
      )}
      {canViewTransportationBenefitsPage && (
        <Route
          path={'/transportation-benefits'}
          element={<TransportationBenefits />}
        />
      )}
      {canViewServiceLevelModelsPage && (
        <Route
          path={'/service-level-models'}
          element={<ServiceLevelModels />}
        />
      )}
      {canViewAdministrationPage && (
        <Route
          path={'/inventory-management'}
          element={<InventoryManagement />}
        />
      )}
      {canViewWorkFlowsPage && (
        <Route
          path={'/simple-notifications'}
          element={<SimpleNotifications />}
        />
      )}
      {canViewCreateWorkflowsPage && (
        <Route
          path={'/simple-notifications/create'}
          element={<SimplifiedWorkflow isCreate />}
        />
      )}
      {canViewEditWorkflowsPage && (
        <Route
          path={'/simple-notifications/edit'}
          element={<SimplifiedWorkflow isCreate={false} />}
        />
      )}
      {canViewWorkFlowsPage && (
        <Route
          path={'/simple-notifications/execution/:id'}
          element={<WorkflowExecutionDetails />}
        />
      )}
      {canViewAdministrationPage && (
        <Route path={'/permissions'} element={<PermissionsAdmin />} />
      )}
      {canViewAdministrationPage && (
        <Route path={'/lookups'} element={<Lookups />} />
      )}
      {canViewAdministrationPage && (
        <Route
          path={'/application-global-settings'}
          element={<ApplicationGlobalSettings />}
        />
      )}
      {canViewAdministrationPage && (
        <Route path={'/rideshare'} element={<RideshareSettings />} />
      )}

      {/* Workflows */}
      {canViewWorkQueuesPage && (
        <Route path={'/workqueues'} element={<WorkQueues />} />
      )}
      {canViewWorkQueuesPage && (
        <Route path={'/workqueue/:id'} element={<WorkQueue />} />
      )}
      {/* TODO: Update when permission set added canViewWorkFlowsPage */}
      {canViewWorkQueuesPage && (
        <Route path={'/workflows'} element={<WorkFlows />} />
      )}
      {/* TODO: Update when permission set added canViewCreateWorkflowsPage */}
      {canViewWorkQueuesPage && (
        <Route path={'/workflow/create'} element={<EditWorkflow isCreate />} />
      )}
      {canViewEditWorkflowsPage && (
        <Route
          path={'/workflow/edit'}
          element={<EditWorkflow isCreate={false} />}
        />
      )}

      {/* AI Services */}
      {canViewAiServicesPage && (
        <Route path={'/ai-services'} element={<AiServicesPage />} />
      )}

      <Route path="*" element={<Navigate to={'/'} replace />} />
    </Routes>
  );

  return canManageMolinaUsers ? molinaRoutes : allRoutes;
};

export default HomeRouting;
